// Single source of truth for the `<meta name="robots">` tag content.
//
// This is a plain utility (NOT a composable) on purpose: `useHead(() => {...})`
// callbacks are re-invoked reactively outside of the component's Nuxt
// context, so calling a composable like `useRuntimeConfig()` from inside one
// throws "A composable that requires access to the Nuxt instance was called
// outside of a plugin, Nuxt hook, Nuxt middleware, or Vue setup function."
// Callers must resolve `env` themselves (e.g. `useRuntimeConfig().public.env`)
// in their component's setup scope and pass it in here.
//
// Rule: environment is the ONLY thing that decides this tag's content.
// Every other meta tag is always present regardless of environment; robots
// is the one exception, toggled solely by `env`:
//   - "production" -> always print the permissive/index directive.
//   - anything else ("development", staging, undefined, ...) -> noindex.

export const PRODUCTION_ROBOTS_CONTENT =
	"index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1";
export const NOINDEX_ROBOTS_CONTENT = "noindex,nofollow";

/** Treat anything that isn't explicitly "production" as non-production. */
export const isProductionEnv = (env?: string | null): boolean =>
	env === "production";

/**
 * Resolve the deployment environment at request time on the server.
 * Checks Cloudflare Worker bindings first, then Nuxt runtime config, then
 * `process.env` — so a build without ENVIRONMENT=production does not
 * permanently bake in noindex headers.
 */
export const resolveServerEnv = (event?: {
	context?: { cloudflare?: { env?: { ENVIRONMENT?: string } } };
}): string | undefined => {
	const cfEnv = event?.context?.cloudflare?.env?.ENVIRONMENT;
	if (cfEnv) return cfEnv;

	try {
		// useRuntimeConfig is auto-imported in Nuxt server context.
		const config = useRuntimeConfig(event);
		if (config.public.env) return config.public.env as string;
	} catch {
		// Not in a Nuxt request context — fall through to process.env.
	}

	return process.env.ENVIRONMENT;
};

/** Returns a ready-to-spread `useHead` meta entry for the `robots` tag. */
export const getRobotsMeta = (env?: string | null) => ({
	name: "robots",
	content: isProductionEnv(env)
		? PRODUCTION_ROBOTS_CONTENT
		: NOINDEX_ROBOTS_CONTENT
});
